import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const POLL_INTERVAL_MS = 7000;

/** Polling-based message thread for a single (unlocked) application. Used both embedded inside
 * an application row and inside the dedicated Messages tab. */
export default function MessageThread({ applicationId, compact = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = () => {
    api.get(`/messages/${applicationId}`).then(({ data }) => setMessages(data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [applicationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/${applicationId}`, { content: draft.trim(), origin_url: window.location.origin });
      setMessages((prev) => [...(prev || []), data]);
      setDraft("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, t));
    } finally {
      setSending(false);
    }
  };

  if (messages === null) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#4ba3b7]" /></div>;

  return (
    <div data-testid={`message-thread-${applicationId}`}>
      <div className={`overflow-y-auto space-y-2 mb-3 ${compact ? "max-h-52" : "max-h-[50vh]"}`} data-testid={`message-thread-list-${applicationId}`}>
        {messages.length === 0 ? (
          <p className="text-sm text-[#2B2B2B]/40" data-testid={`message-thread-empty-${applicationId}`}>{t("messages.noMessagesYet")}</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`} data-testid={`message-bubble-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender_id === user.id ? "bg-[#4ba3b7] text-white" : "bg-[#FAFAFA] border border-black/5 text-[#2B2B2B]"}`}>
                <p className="whitespace-pre-wrap break-words">{m.content}</p>
                <p className={`text-[10px] mt-1 ${m.sender_id === user.id ? "text-white/70" : "text-[#2B2B2B]/40"}`}>
                  {new Date(m.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={handleSend} className="flex items-center gap-2">
        <input type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
               placeholder={t("messages.writeMessagePlaceholder")}
               className="flex-1 rounded-xl border border-black/10 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ba3b7]/40"
               data-testid={`message-thread-input-${applicationId}`} />
        <Button type="submit" size="sm" disabled={sending || !draft.trim()} className="rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] shrink-0"
                data-testid={`message-thread-send-button-${applicationId}`}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
